import { Fragment, createElement } from 'react';
import type { ReactNode } from 'react';
import type { ChartTypeId, Timeframe } from 'hopcharts';
import { en } from '../locales/en';
import type { MessageKey, Messages } from '../locales/en';

// Same codes, URL scheme and cookies as www.cryptohopper.com (ch-sanity-lp: apps/web/proxy.ts and
// components/layout/LanguageSwitcher.tsx), so a visitor keeps their language across both sites.

export const LOCALES = ['en', 'nl', 'es', 'fr', 'de', 'pl', 'it', 'pt-br', 'zh-cn', 'ja', 'ko', 'id'] as const;
export type Locale = (typeof LOCALES)[number];

/**
 * Locales whose catalog covers the whole site: offered in the switcher, chosen from the cookie or the
 * browser language, and listed as hreflang alternates. Any other locale with a catalog is reachable
 * only by its URL (/nl/…) and is noindex, so production never serves a half-translated site.
 */
export const RELEASED: readonly Locale[] = ['nl', 'es', 'fr', 'de', 'pl', 'pt-br', 'it', 'ja', 'zh-cn', 'ko', 'id'];

/** Name of each language in that language, for the switcher. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  nl: 'Nederlands',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pl: 'Polski',
  it: 'Italiano',
  'pt-br': 'Português (Brasil)',
  'zh-cn': '简体中文',
  ja: '日本語',
  ko: '한국어',
  id: 'Bahasa Indonesia',
};

const CATALOGS: Partial<Record<Locale, () => Promise<{ messages: Messages }>>> = {
  nl: () => import('../locales/nl'),
  es: () => import('../locales/es'),
  fr: () => import('../locales/fr'),
  de: () => import('../locales/de'),
  pl: () => import('../locales/pl'),
  'pt-br': () => import('../locales/pt-br'),
  it: () => import('../locales/it'),
  ja: () => import('../locales/ja'),
  'zh-cn': () => import('../locales/zh-cn'),
  ko: () => import('../locales/ko'),
  id: () => import('../locales/id'),
};

// Browser and legacy codes that www maps onto its own (proxy.ts LOCALE_MAPPINGS / LEGACY_LOCALE_MAPPINGS).
const ALIASES: Record<string, Locale> = { pt: 'pt-br', zh: 'zh-cn', 'zh-tw': 'zh-cn' };

/** Joomla's setlanguage values, as www's switcher writes them for the PHP pages. */
const JOOMLA: Record<Locale, string> = {
  en: 'en-GB',
  nl: 'nl-NL',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  pl: 'pl-PL',
  it: 'it-IT',
  'pt-br': 'pt-BR',
  'zh-cn': 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
  id: 'id-ID',
};

const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/** A language code from a cookie or the browser, matched the way www does: exact, alias, then prefix (nl-BE → nl). */
export function matchLocale(code: string): Locale | null {
  const c = code.trim().toLowerCase();
  if (isLocale(c)) return c;
  if (ALIASES[c]) return ALIASES[c];
  const prefix = c.split('-')[0];
  if (isLocale(prefix)) return prefix;
  return ALIASES[prefix] ?? null;
}

function cookieValue(cookies: string, name: string): string | null {
  for (const part of cookies.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

/**
 * The visitor's language when the URL names none: the preferredLanguage cookie, then the shared
 * setlanguage cookie (domain .cryptohopper.com), then the browser's languages; only released ones count.
 */
export function preferredLocale(cookies: string, languages: readonly string[], released: readonly Locale[] = RELEASED): Locale {
  const usable = (code: string | null): Locale | null => {
    const locale = code ? matchLocale(code) : null;
    return locale && (locale === 'en' || released.includes(locale)) ? locale : null;
  };
  return usable(cookieValue(cookies, 'preferredLanguage')) ?? usable(cookieValue(cookies, 'setlanguage')) ?? languages.map(usable).find((l) => l !== null) ?? 'en';
}

/** Locale named by the first path segment (/nl/chart → nl), or null for unprefixed (English) paths. */
export function localeFromPath(pathname: string): Locale | null {
  const first = pathname.split('/')[1] ?? '';
  return isLocale(first) ? first : null;
}

/**
 * The path without its locale prefix: /nl/chart → /chart, /nl → /. Leading slashes are collapsed to one,
 * so /en//host/x cannot become the protocol-relative URL //host/x when the result is navigated to.
 */
export function stripLocale(pathname: string): string {
  const rest = localeFromPath(pathname) ? pathname.replace(/^\/[^/]+/, '') : pathname;
  return rest.replace(/^\/+/, '/') || '/';
}

export function localizedPath(locale: Locale, path: string): string {
  return locale === 'en' ? path : `/${locale}${path === '/' ? '' : path}`;
}

/**
 * Where a page load starts: the locale to render, or a URL to go to first. /en/… drops its prefix
 * and an unprefixed path moves to the visitor's language, as on www. Embeds are never redirected.
 */
export function startLocale(
  location: { pathname: string; search: string; hash: string },
  cookies: string,
  languages: readonly string[],
): { locale: Locale; redirect?: string } {
  const { pathname, search, hash } = location;
  const fromPath = localeFromPath(pathname);
  if (fromPath === 'en') return { locale: 'en', redirect: stripLocale(pathname) + search + hash };
  if (fromPath) return { locale: fromPath };
  if (pathname.startsWith('/embed/')) return { locale: 'en' };
  const preferred = preferredLocale(cookies, languages);
  return preferred === 'en' ? { locale: 'en' } : { locale: preferred, redirect: localizedPath(preferred, pathname) + search + hash };
}

let current: Locale = 'en';
let messages: Messages = en;

export function locale(): Locale {
  return current;
}

/** BCP 47 tag for <html lang>, hreflang and Intl (pt-br → pt-BR). */
export function languageTag(l: Locale = current): string {
  const [lang, region] = l.split('-');
  return region ? `${lang}-${region.toUpperCase()}` : lang;
}

/** Loads the catalog before anything renders; a locale without one falls back to English. */
export async function initI18n(l: Locale): Promise<void> {
  const load = CATALOGS[l];
  current = load ? l : 'en';
  messages = load ? (await load()).messages : en;
  document.documentElement.lang = languageTag(current);
  if (current !== 'en' && !RELEASED.includes(current)) {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.append(robots);
  }
}

/** Router basename for the current locale: undefined for English, /nl for Dutch. */
export function basename(): string | undefined {
  return current === 'en' ? undefined : `/${current}`;
}

/** Translated text; {name} placeholders take values from `vars`. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const text = messages[key] ?? en[key];
  return vars ? text.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match)) : text;
}

/** Keys that come in plural forms: "markets.coins" for markets.coins.one / markets.coins.other. */
export type PluralKey = { [K in MessageKey]: K extends `${infer Base}.other` ? Base : never }[MessageKey];

/**
 * The plural form of `base` that the page language uses for `count` (Intl.PluralRules: one, few,
 * many, other…), falling back to .other; {count} is filled in, formatted like the rest of the site.
 */
export function plural(base: PluralKey, count: number, vars: Record<string, string | number> = {}): string {
  const form = `${base}.${new Intl.PluralRules(languageTag()).select(count)}`;
  const key = (form in messages ? form : `${base}.other`) as MessageKey;
  return t(key, { count: count.toLocaleString('en-US'), ...vars });
}

const INTERVAL_UNITS = { m: 'minute', h: 'hour', D: 'day', W: 'week', M: 'month' } as const;

/** A chart interval's name in the page language ("5 Minuten", "4 hours"), from the browser's own unit names. */
export function intervalName(tf: Timeframe, tag = languageTag()): string {
  const unit = INTERVAL_UNITS[tf.slice(-1) as keyof typeof INTERVAL_UNITS];
  return new Intl.NumberFormat(tag, { style: 'unit', unit, unitDisplay: 'long' }).format(parseInt(tf, 10));
}

/** A chart type's name in the page language (HopCharts' CHART_TYPE_LABELS are English only). */
export function chartTypeName(id: ChartTypeId): string {
  return t(`chartType.${id}`);
}

/** Translated text with React nodes in its placeholders, e.g. a formatted price inside a sentence. */
export function tx(key: MessageKey, nodes: Record<string, ReactNode>): ReactNode {
  const text = messages[key] ?? en[key];
  return text.split(/\{(\w+)\}/g).map((part, i) => createElement(Fragment, { key: i }, i % 2 ? (nodes[part] ?? `{${part}}`) : part));
}

/** A www.cryptohopper.com URL in the visitor's language (www has no Italian, so that stays English). */
export function wwwUrl(path = '/'): string {
  const prefix = current === 'en' || current === 'it' ? '' : `/${current}`;
  return `https://www.cryptohopper.com${prefix}${path === '/' ? '' : path}`;
}

// Help-center languages checked to exist (support.cryptohopper.com/<code>/ answers 200); others get English.
const SUPPORT_LOCALES: readonly Locale[] = ['en', 'nl', 'es', 'de', 'pt-br', 'zh-cn'];

/** Cryptohopper Support (Intercom help center) in the given language where it has one. */
export function supportUrl(l: Locale = current): string {
  return `https://support.cryptohopper.com/${SUPPORT_LOCALES.includes(l) ? l : 'en'}/`;
}

/** Remembers the choice in both of www's cookies, then reloads the current page in that language. */
export function switchLocale(next: Locale): void {
  const domain = window.location.hostname.endsWith('cryptohopper.com') ? '; domain=.cryptohopper.com' : '';
  const attrs = `; path=/; max-age=${COOKIE_MAX_AGE}${domain}; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  if (domain) {
    // A same-name cookie scoped to this host only would be sent first and shadow the shared one.
    document.cookie = 'preferredLanguage=; path=/; max-age=0';
    document.cookie = 'setlanguage=; path=/; max-age=0';
  }
  document.cookie = `preferredLanguage=${next}${attrs}`;
  document.cookie = `setlanguage=${JOOMLA[next]}${attrs}`;
  const { pathname, search, hash } = window.location;
  window.location.assign(localizedPath(next, stripLocale(pathname)) + search + hash);
}

/** Locales the switcher offers: English, the released ones, and the current one while it is previewed. */
export function switcherLocales(): Locale[] {
  return LOCALES.filter((l) => l === 'en' || RELEASED.includes(l) || l === current);
}
